<template>
  <form
    id="invite-external-user"
    class="w-full flex flex-col md:flex-row gap-3 mb-4"
    @submit="onSubmit"
  >
    <Input
      id="email"
      v-model="email"
      name="email"
      type="email"
      v-bind="emailAttrs"
      placeholder="Email address"
    />
    <Select v-model="role" default-value="viewer" :disabled="isInvitePending">
      <SelectTrigger class="w-40">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="viewer">Viewer</SelectItem>
        <SelectItem value="editor">
          Editor
          <Badge v-if="currentSubscriptionPlan">Billable</Badge>
        </SelectItem>
      </SelectContent>
    </Select>
    <div>
      <Button type="submit" form="invite-external-user">Invite</Button>
    </div>
  </form>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useQueryClient, useMutation } from '@tanstack/vue-query';
import { useForm } from 'vee-validate';
import { Input } from '@ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ui/components/select';
import { Button } from '@ui/components/button';
import { Badge } from '@ui/components/badge';
import requests from '@vuex/requests';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import { queries } from '@/queries/index.js';
import { toast } from 'vue-sonner';
import mixpanelSingleton from '@/mixpanel/mixpanelSingleton.js';

const queryClient = useQueryClient();
const store = useStore();

const currentSubscriptionPlan = computed(
  () => store.getters['workspace/getCurrentSubscriptionPlan']
);

const validationSchema = toTypedSchema(
  z.object({
    email: z.string().min(1, { message: 'Email is required' }).email(),
    role: z.enum(['viewer', 'editor']),
  })
);

const { handleSubmit, defineField, resetForm } = useForm({
  validationSchema,
  initialValues: {
    email: '',
    role: 'viewer',
  },
});

const [email, emailAttrs] = defineField('email');
const [role] = defineField('role');

const { mutate: inviteExternalUser, isPending: isInvitePending } = useMutation({
  mutationFn: async payload => {
    await requests.projects.inviteExternalUser(payload);
  },
  onSuccess: async () => {
    await queryClient.invalidateQueries({
      queryKey: queries.project.externalUsers(
        store.getters['user/projects/getOpen']
      ).queryKey,
    });
    await queryClient.invalidateQueries({
      queryKey: queries.project.pendingInvitations(
        store.getters['user/projects/getOpen']
      ).queryKey,
    });
    resetForm();
    // Mix Panel Event
    mixpanelSingleton.track('button_clicked', {
      type: 'share',
      subtype: 'invite',
    });
  },
  onError: err => {
    console.error(err);
    if (err?.response?.data?.message) {
      toast.error(err.response.data.message);
    } else {
      toast.error('Inviting an external user failed.');
    }
  },
});

const onSubmit = handleSubmit(values => {
  inviteExternalUser({
    ...values,
    hash: store.getters['user/projects/getOpen'],
    workspaceId: store.getters['workspace/getId'],
  });
});
</script>
